import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, CircleDashed, ExternalLink, FileText, Loader2, RotateCcw, Unlock, XCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { useAuth } from '../../hooks/useAuth';
import { useNotifications } from '../../store/useStore';
import { cn } from '../../lib/cn';
import { eventiService } from '../../lib/eventi/eventiService';
import { messaggioErrore } from '../../lib/eventi/errori';
import { statoFattura } from '../../lib/eventi/fatturaOrdine';
import type { BigliettoEvento } from '../../lib/eventi/tipi';
import { DialogFatturaOrdine } from './DialogFatturaOrdine';

type Props = {
  ordine: BigliettoEvento['ordine'];
  onCambiata: () => Promise<void>;
};

/** Lo stato della fattura in una riga: icona, testo, colore. */
function Stato({ icona: Icona, testo, sotto, tono }: { icona: typeof FileText; testo: string; sotto?: string; tono: 'neutro' | 'ok' | 'attenzione' | 'errore' }) {
  const colori = {
    neutro: 'text-neutral-600',
    ok: 'text-green-700',
    attenzione: 'text-amber-800',
    errore: 'text-red-700',
  }[tono];
  return (
    <div className={cn('flex min-w-0 items-start gap-2 text-sm', colori)}>
      <Icona className={cn('mt-0.5 h-4 w-4 flex-shrink-0', Icona === Loader2 && 'animate-spin')} />
      <div className="min-w-0">
        <p className="font-medium">{testo}</p>
        {sotto && <p className="text-xs opacity-90">{sotto}</p>}
      </div>
    </div>
  );
}

/**
 * La fattura di un ordine del sito: lo stato a sinistra, l'azione a destra.
 * La usano la scheda Biglietti di un evento e la pagina Fatture (Ordini dal
 * sito): stessi dati, stessa finestra. Se l'esito e' incerto, solo un
 * amministratore puo' sbloccare, dopo aver controllato su Fatture in Cloud.
 */
export function FatturaOrdine({ ordine, onCambiata }: Props) {
  const { profile } = useAuth();
  const { addNotification } = useNotifications();
  const [aperta, setAperta] = useState(false);
  const [sblocco, setSblocco] = useState<'chiedi' | 'in_corso' | null>(null);
  const s = statoFattura(ordine, new Date());

  const sblocca = async () => {
    setSblocco('in_corso');
    try {
      await eventiService.sbloccaFatturaOrdine(ordine.id);
      addNotification({ type: 'success', title: 'Fattura sbloccata', message: `L'ordine n. ${ordine.numero} si può fatturare di nuovo.` });
    } catch (e) {
      addNotification({ type: 'error', title: 'Non riesco a sbloccare', message: messaggioErrore(e) });
    } finally {
      setSblocco(null);
      await onCambiata();
    }
  };

  let stato;
  let azione = null;

  if (s.stato === 'nessuna') {
    stato = <Stato icona={XCircle} tono="neutro" testo="Nessuna fattura" sotto="Ordine non pagato, annullato o rimborsato." />;
  } else if (s.stato === 'gratuito') {
    stato = <Stato icona={XCircle} tono="neutro" testo="Ordine gratuito" sotto="Niente da fatturare." />;
  } else if (s.stato === 'emessa' || s.stato === 'nota_di_credito') {
    stato = s.stato === 'emessa'
      ? <Stato icona={CheckCircle2} tono="ok" testo={`Fattura ${s.numero ?? ''} su Fatture in Cloud`} sotto="L'invio allo SDI si fa da Fatture in Cloud." />
      : <Stato icona={AlertTriangle} tono="errore" testo={`Fattura ${s.numero ?? ''}: ordine rimborsato`} sotto="Serve una nota di credito, da fare su Fatture in Cloud." />;
    azione = (
      <Button asChild variant="outline" size="sm">
        <Link to={`/invoices?apri=${s.id}`}>
          <ExternalLink className="mr-2 h-4 w-4" /> Apri fattura
        </Link>
      </Button>
    );
  } else if (s.stato === 'in_creazione') {
    stato = <Stato icona={Loader2} tono="neutro" testo="Fattura in creazione…" />;
  } else if (s.stato === 'incerta') {
    stato = <Stato icona={AlertTriangle} tono="attenzione" testo="Fattura: esito incerto" sotto={s.testo} />;
    if (profile?.role === 'admin') {
      azione = sblocco === 'chiedi' ? (
        <div className="flex flex-wrap items-center justify-end gap-2">
          <span className="text-xs text-amber-800">Hai controllato su Fatture in Cloud? Al prossimo tentativo, se la fattura c'è, Pixel la collega.</span>
          <Button size="sm" variant="outline" onClick={() => setSblocco(null)}>
            Annulla
          </Button>
          <Button size="sm" onClick={() => void sblocca()}>
            Sblocca
          </Button>
        </div>
      ) : (
        <Button size="sm" variant="outline" onClick={() => setSblocco('chiedi')} disabled={sblocco === 'in_corso'}>
          {sblocco === 'in_corso' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Unlock className="mr-2 h-4 w-4" />}
          Sblocca
        </Button>
      );
    }
  } else {
    stato = s.stato === 'errore'
      ? <Stato icona={AlertTriangle} tono="errore" testo="Ultimo tentativo non riuscito" sotto={s.testo} />
      : <Stato icona={CircleDashed} tono="neutro" testo="Da fatturare" />;
    azione = (
      <Button size="sm" onClick={() => setAperta(true)}>
        {s.stato === 'errore' ? <RotateCcw className="mr-2 h-4 w-4" /> : <FileText className="mr-2 h-4 w-4" />}
        {s.stato === 'errore' ? 'Riprova' : 'Prepara fattura'}
      </Button>
    );
  }

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      {stato}
      {azione && <div className="flex-shrink-0">{azione}</div>}
      {(s.stato === 'da_fare' || s.stato === 'errore') && (
        <DialogFatturaOrdine ordine={ordine} aperta={aperta} onChiudi={() => setAperta(false)} onCambiata={onCambiata} />
      )}
    </div>
  );
}
